import React, { useEffect, useMemo, useState } from 'react';
import { axiosInstance } from '../AuthContext';

const labelFor = (value) => value.replace(/_/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());
const detailMessage = (detail, fallback) => {
  if (detail?.issues?.length) {
    const issue = detail.issues[0];
    return `${detail.message || fallback} ${labelFor(issue.field)} does not support “${issue.unsupported_value}”.`;
  }
  return detail?.message || (typeof detail === 'string' ? detail : fallback);
};
const newRule = (catalog, index = 0) => {
  const field = catalog?.fields?.[index]?.field || 'market_cap';
  const metadata = catalog?.fields?.find((item) => item.field === field) || { kind: 'numeric', unit: 'currency' };
  return {
    id: `rule.${Date.now()}.${index}`,
    field,
    operator: metadata.kind === 'numeric' ? 'gte' : 'eq',
    value: metadata.kind === 'boolean' ? false : (metadata.kind === 'classification' ? (metadata.allowed_values?.[0]?.value || '') : ''),
    unit: metadata.unit,
  };
};

const definitionPayload = (name, description, rules) => ({
  schema_version: 'custom-screening-v1',
  name: name.trim(),
  description: description.trim(),
  missing_data_policy: 'reject_required_field',
  required_fields: [...new Set(rules.map((rule) => rule.field))].sort(),
  criteria: rules,
});

const CustomScreeningWorkspace = ({
  definitions = [],
  catalog,
  selectedDefinitionId,
  onSelectDefinition,
  onDefinitionsChanged,
  onUseSymbols,
}) => {
  const selected = definitions.find((item) => String(item.id) === String(selectedDefinitionId));
  const [lifecycleView, setLifecycleView] = useState('ACTIVE');
  const [pendingLifecycleAction, setPendingLifecycleAction] = useState(null);
  const [lifecycleReason, setLifecycleReason] = useState('');
  const [name, setName] = useState('My custom screening');
  const [description, setDescription] = useState('Tenant-owned candidate selection rules');
  const [rules, setRules] = useState(() => [newRule(catalog)]);
  const [parentDefinitionId, setParentDefinitionId] = useState(null);
  const [expertText, setExpertText] = useState('');
  const [candidates, setCandidates] = useState([]);
  const [evaluation, setEvaluation] = useState(null);
  const [selectedSymbols, setSelectedSymbols] = useState([]);
  const [message, setMessage] = useState('');
  const [working, setWorking] = useState(false);
  const activeDefinitions = definitions.filter((item) => item.lifecycle_state !== 'ARCHIVED');
  const archivedDefinitions = definitions.filter((item) => item.lifecycle_state === 'ARCHIVED');
  const visibleDefinitions = lifecycleView === 'ARCHIVED' ? archivedDefinitions : activeDefinitions;

  useEffect(() => {
    if (!catalog || rules.length !== 1 || rules[0].value !== '') return;
    setRules([newRule(catalog)]);
  }, [catalog]); // eslint-disable-line react-hooks/exhaustive-deps

  const payload = useMemo(
    () => definitionPayload(name, description, rules),
    [name, description, rules],
  );

  useEffect(() => setExpertText(JSON.stringify(payload, null, 2)), [payload]);

  useEffect(() => {
    setCandidates([]);
    setEvaluation(null);
    setSelectedSymbols([]);
  }, [selectedDefinitionId]);

  const loadAsChild = () => {
    if (!selected) return;
    const canonical = selected.canonical_configuration;
    setName(`${canonical.name} copy`);
    setDescription(canonical.description || 'Immutable revision');
    setRules(canonical.criteria.map((rule) => ({ ...rule })));
    setParentDefinitionId(selected.id);
    setMessage(selected.compatibility?.compatible === false
      ? 'Loaded as an immutable child draft. Review every unsupported value and explicitly accept a replacement; the original remains unchanged.'
      : 'Loaded as a new immutable child. The original remains unchanged.');
  };

  const beginLifecycleAction = (action) => {
    setPendingLifecycleAction(action);
    setLifecycleReason(action === 'archive'
      ? 'No longer needed in active Custom Screening selection'
      : 'Restored for active Custom Screening selection');
  };

  const applyLifecycleAction = async () => {
    if (!selected || !pendingLifecycleAction || !lifecycleReason.trim()) return;
    const action = pendingLifecycleAction;
    setWorking(true);
    setMessage('');
    try {
      const response = await axiosInstance.post(
        `/api/screening/definitions/${selected.id}/${action}`,
        { reason: lifecycleReason.trim(), idempotency_key: crypto.randomUUID() },
      );
      await onDefinitionsChanged?.();
      setLifecycleView(action === 'archive' ? 'ARCHIVED' : 'ACTIVE');
      onSelectDefinition?.(String(response.data.id));
      setMessage(action === 'archive'
        ? 'Definition archived. It is hidden from active selection, but its immutable evidence and lineage remain available here.'
        : 'Definition restored to active Custom Screening selection.');
      setPendingLifecycleAction(null);
      setLifecycleReason('');
      setCandidates([]);
      setEvaluation(null);
    } catch (error) {
      const detail = error.response?.data?.detail;
      setMessage(detail?.message || detail || `Unable to ${action} this definition.`);
    } finally {
      setWorking(false);
    }
  };

  const acceptSuggestion = (issue) => {
    setRules((current) => current.map((rule) => {
      if (rule.id !== issue.criterion_id) return rule;
      const value = Array.isArray(rule.value)
        ? rule.value.map((item) => item === issue.unsupported_value ? issue.suggested_value : item)
        : issue.suggested_value;
      return { ...rule, value };
    }));
    setMessage(`You accepted ${labelFor(issue.suggested_value)} for this new child draft. The historical parent remains unchanged.`);
  };

  const updateRule = (index, key, value) => {
    setRules((current) => current.map((rule, ruleIndex) => {
      if (ruleIndex !== index) return rule;
      if (key === 'operator') {
        const metadata = catalog.fields.find((item) => item.field === rule.field);
        if (metadata?.kind !== 'classification') return { ...rule, operator: value };
        const first = Array.isArray(rule.value) ? rule.value[0] : rule.value;
        return { ...rule, operator: value, value: ['in', 'not_in'].includes(value) ? (first ? [first] : []) : (first || '') };
      }
      if (key !== 'field') return { ...rule, [key]: value };
      const metadata = catalog.fields.find((item) => item.field === value);
      return {
        ...rule,
        field: value,
        operator: metadata.kind === 'numeric' ? 'gte' : 'eq',
        unit: metadata.unit,
        value: metadata.kind === 'boolean' ? false : (metadata.kind === 'classification' ? (metadata.allowed_values?.[0]?.value || '') : ''),
      };
    }));
  };

  const save = async (event) => {
    event.preventDefault();
    setWorking(true);
    setMessage('');
    try {
      const parsed = JSON.parse(expertText);
      const response = await axiosInstance.post('/api/screening/definitions', {
        definition: parsed,
        parent_definition_id: parentDefinitionId,
      });
      await onDefinitionsChanged?.();
      onSelectDefinition?.(String(response.data.id));
      setParentDefinitionId(null);
      setMessage(`Saved immutable definition ${String(response.data.configuration_hash).slice(0, 12)}…`);
    } catch (error) {
      const detail = error.response?.data?.detail;
      setMessage(detailMessage(detail, 'The custom screening definition is invalid.'));
    } finally {
      setWorking(false);
    }
  };

  const evaluate = async () => {
    if (!selectedDefinitionId) return;
    setWorking(true);
    setMessage('');
    try {
      const response = await axiosInstance.get(`/api/screening/definitions/${selectedDefinitionId}/candidates`);
      setCandidates(response.data.stocks || []);
      setEvaluation(response.data);
      setSelectedSymbols([]);
      if (response.data.status === 'incompatible_definition') {
        const issue = response.data.compatibility?.issues?.[0];
        setMessage(`Incompatible with the current catalog: ${labelFor(issue?.field || 'field')} does not support “${issue?.unsupported_value || 'this value'}”. No rule was silently translated.`);
      } else if (response.data.status === 'no_matches') {
        setMessage(`No matching stocks. Evaluated for ${response.data.evaluation_date || 'the active session'}.`);
      } else {
        setMessage(`Found ${response.data.match_count} matching stock${response.data.match_count === 1 ? '' : 's'} for ${response.data.evaluation_date || 'the active session'}.`);
      }
    } catch (error) {
      setCandidates([]);
      setEvaluation({ status: 'evaluation_error' });
      const detail = error.response?.data?.detail;
      if (error.response?.status === 503 && detail?.code === 'screening_data_unavailable') {
        setMessage('Stock screening data is temporarily unavailable. Your definition is saved; no legacy or provider fallback was used.');
      } else {
        setMessage(detail?.message || detail || 'Custom screening could not be evaluated.');
      }
    } finally {
      setWorking(false);
    }
  };

  if (!catalog) return (
    <section aria-label="Modern Custom Screening" className="rounded-2xl border border-amber-300 bg-amber-50 p-5 dark:border-amber-800 dark:bg-amber-950/30">
      <h3 className="text-lg font-black">Custom Screening definitions</h3>
      <p className="mt-2 text-sm leading-6">The ACTIVE screening catalog is unavailable. Saved immutable definitions remain inspectable, but categorical authoring and evaluation are disabled. No legacy or provider fallback is used.</p>
      <div className="mt-4 flex flex-wrap gap-2" role="group" aria-label="Custom Screening lifecycle view">
        <button type="button" onClick={() => setLifecycleView('ACTIVE')} aria-pressed={lifecycleView === 'ACTIVE'} className="rounded-lg border px-3 py-2 text-sm font-bold">Active ({activeDefinitions.length})</button>
        <button type="button" onClick={() => setLifecycleView('ARCHIVED')} aria-pressed={lifecycleView === 'ARCHIVED'} className="rounded-lg border px-3 py-2 text-sm font-bold">Archived ({archivedDefinitions.length})</button>
      </div>
      <label className="mt-4 block text-sm font-bold" htmlFor="custom-screening-existing-unavailable">{lifecycleView === 'ARCHIVED' ? 'Archived definitions' : 'Active definitions'}</label>
      <select id="custom-screening-existing-unavailable" value={selectedDefinitionId || ''} onChange={(event) => onSelectDefinition?.(event.target.value)} className="mt-2 w-full rounded-xl border bg-white p-3 dark:border-slate-700 dark:bg-slate-900">
        <option value="">Select a Custom Screening definition</option>
        {visibleDefinitions.map((definition) => <option key={definition.id} value={definition.id}>{definition.name}</option>)}
      </select>
      {selected && <div className="mt-3 rounded-xl bg-white/80 p-3 text-xs dark:bg-slate-900/70"><p><strong>{selected.name}</strong> · {selected.schema_version} · {selected.lifecycle_state || 'ACTIVE'}</p><p className="mt-1 break-all">Hash: {selected.configuration_hash}</p>{selected.parent_definition_id && <p className="mt-1 break-all">Parent: {selected.parent_definition_id}</p>}</div>}
    </section>
  );

  return (
    <section aria-label="Modern Custom Screening" className="space-y-5">
      <div className="rounded-2xl border border-blue-200 bg-blue-50 p-5 dark:border-blue-900 dark:bg-blue-950/30">
        <h3 className="text-lg font-black">Custom Screening</h3>
        <p className="mt-2 text-sm leading-6">Choose which stocks are candidates. These rules are separate from the Trading Strategy that decides when to buy or sell. Saved definitions are immutable and use only an active production screening generation.</p>
        <p className="mt-2 text-xs text-slate-600 dark:text-slate-300">Catalog {catalog.schema_version} · vocabulary {catalog.vocabulary_version} · screening session {catalog.generation?.technical_session_date}</p>
        <div className="mt-4 flex flex-wrap gap-2" role="group" aria-label="Custom Screening lifecycle view">
          <button type="button" onClick={() => { setLifecycleView('ACTIVE'); onSelectDefinition?.(''); }} aria-pressed={lifecycleView === 'ACTIVE'} className={`rounded-lg border px-3 py-2 text-sm font-bold ${lifecycleView === 'ACTIVE' ? 'border-blue-600 bg-blue-600 text-white' : 'bg-white dark:bg-slate-900'}`}>Active ({activeDefinitions.length})</button>
          <button type="button" onClick={() => { setLifecycleView('ARCHIVED'); onSelectDefinition?.(''); }} aria-pressed={lifecycleView === 'ARCHIVED'} className={`rounded-lg border px-3 py-2 text-sm font-bold ${lifecycleView === 'ARCHIVED' ? 'border-blue-600 bg-blue-600 text-white' : 'bg-white dark:bg-slate-900'}`}>Archived ({archivedDefinitions.length})</button>
        </div>
        <label className="mt-4 block text-sm font-bold" htmlFor="custom-screening-existing">{lifecycleView === 'ARCHIVED' ? 'Archived definitions' : 'Active definitions'}</label>
        <select id="custom-screening-existing" value={selectedDefinitionId || ''} onChange={(event) => onSelectDefinition?.(event.target.value)} className="mt-2 w-full rounded-xl border bg-white p-3 dark:border-slate-700 dark:bg-slate-900">
          <option value="">Select a Custom Screening definition</option>
          {visibleDefinitions.map((definition) => <option key={definition.id} value={definition.id}>{definition.name}{definition.compatibility?.compatible === false ? ' · incompatible' : ''}</option>)}
        </select>
        {selected && (
          <div className="mt-3 rounded-xl bg-white/80 p-3 text-xs dark:bg-slate-900/70">
            <p><strong>{selected.name}</strong> · {selected.schema_version} · <span className={selected.lifecycle_state === 'ARCHIVED' ? 'font-black text-amber-700 dark:text-amber-300' : 'font-black text-emerald-700 dark:text-emerald-300'}>{selected.lifecycle_state || 'ACTIVE'}</span></p>
            <p className="mt-1 break-all">Hash: {selected.configuration_hash}</p>
            {selected.parent_definition_id && <p className="mt-1 break-all">Parent: {selected.parent_definition_id}</p>}
            {selected.lifecycle?.effective_at && <p className="mt-1">Lifecycle effective: {selected.lifecycle.effective_at}</p>}
            {selected.lifecycle_state === 'ARCHIVED' && <p className="mt-2 rounded-lg bg-amber-50 p-2 text-amber-900 dark:bg-amber-950/40 dark:text-amber-100">Archived definitions are read-only evidence. They are hidden from active selection and cannot be evaluated until explicitly restored.</p>}
            {selected.compatibility?.compatible === false && (
              <div role="alert" className="mt-3 rounded-lg border border-amber-300 bg-amber-50 p-3 text-amber-900 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-100">
                <p className="font-bold">Incompatible with the current categorical vocabulary</p>
                {selected.compatibility.issues.map((issue) => (
                  <div key={`${issue.criterion_id}-${issue.unsupported_value}`} className="mt-2">
                    <p>{labelFor(issue.field)} does not support “{issue.unsupported_value}”. Evaluation cannot produce valid matches from this immutable rule.</p>
                    {issue.suggested_value && <p className="mt-1">Reviewed replacement available for a new clone: <strong>{issue.suggested_label}</strong> ({issue.suggested_value}).</p>}
                  </div>
                ))}
              </div>
            )}
            <button type="button" onClick={loadAsChild} className="mt-3 rounded-lg border px-3 py-2 font-bold">{selected.compatibility?.compatible === false ? 'Clone and update' : 'Clone or edit as new'}</button>
            <button type="button" onClick={() => beginLifecycleAction(selected.lifecycle_state === 'ARCHIVED' ? 'restore' : 'archive')} className={`ml-2 mt-3 rounded-lg border px-3 py-2 font-bold ${selected.lifecycle_state === 'ARCHIVED' ? 'border-emerald-500 text-emerald-800 dark:text-emerald-300' : 'border-amber-500 text-amber-900 dark:text-amber-300'}`}>{selected.lifecycle_state === 'ARCHIVED' ? 'Restore definition' : 'Archive definition'}</button>
            {pendingLifecycleAction && (
              <div role="alertdialog" aria-labelledby="custom-screening-lifecycle-confirmation" className="mt-3 rounded-xl border border-amber-400 bg-amber-50 p-4 text-sm text-amber-950 dark:bg-amber-950/40 dark:text-amber-100">
                <p id="custom-screening-lifecycle-confirmation" className="font-black">{pendingLifecycleAction === 'archive' ? `Archive “${selected.name}”?` : `Restore “${selected.name}”?`}</p>
                <p className="mt-1 leading-5">{pendingLifecycleAction === 'archive' ? 'Archiving hides this definition from active selection. It does not delete or change its UUID, rules, hash, parent, or evidence.' : 'Restoring makes this unchanged immutable definition available for active selection again.'}</p>
                <label className="mt-3 block font-bold">Reason<input value={lifecycleReason} onChange={(event) => setLifecycleReason(event.target.value)} maxLength={500} className="mt-1 w-full rounded-lg border bg-white p-2 text-slate-950 dark:bg-slate-900 dark:text-white" /></label>
                <div className="mt-3 flex flex-wrap gap-2">
                  <button type="button" disabled={working || !lifecycleReason.trim()} onClick={applyLifecycleAction} className="rounded-lg bg-amber-700 px-4 py-2 font-bold text-white disabled:opacity-50">Confirm {pendingLifecycleAction}</button>
                  <button type="button" onClick={() => { setPendingLifecycleAction(null); setLifecycleReason(''); }} className="rounded-lg border px-4 py-2 font-bold">Cancel</button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <form onSubmit={save} className="rounded-2xl border border-slate-200 p-5 dark:border-slate-700">
        <h3 className="text-lg font-black">Create a definition</h3>
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          <label className="text-sm font-bold">Name<input value={name} onChange={(event) => setName(event.target.value)} minLength={3} maxLength={100} className="mt-1 w-full rounded-lg border p-2 dark:border-slate-700 dark:bg-slate-900" /></label>
          <label className="text-sm font-bold">Purpose<input value={description} onChange={(event) => setDescription(event.target.value)} className="mt-1 w-full rounded-lg border p-2 dark:border-slate-700 dark:bg-slate-900" /></label>
        </div>
        <p className="mt-4 text-sm font-bold">Required rules</p>
        <p className="text-xs text-slate-500">If a required value is missing, the stock is rejected. Missing values never pass through.</p>
        <div className="mt-3 space-y-3">
          {rules.map((rule, index) => {
            const metadata = catalog.fields.find((item) => item.field === rule.field);
            const operators = catalog.operators[metadata?.kind || 'numeric'];
            const allowedValues = metadata?.allowed_values || [];
            const selectedValues = Array.isArray(rule.value) ? rule.value : [rule.value];
            const unsupportedValues = selectedValues.filter((value) => value && !allowedValues.some((option) => option.value === value));
            return (
              <div key={rule.id} className="grid gap-2 rounded-xl bg-slate-50 p-3 dark:bg-slate-800 sm:grid-cols-[2fr_1fr_2fr_auto]">
                <select aria-label={`Rule ${index + 1} field`} value={rule.field} onChange={(event) => updateRule(index, 'field', event.target.value)} className="min-w-0 w-full rounded-lg border p-2 dark:border-slate-700 dark:bg-slate-900">
                  {catalog.fields.map((field) => <option key={field.field} value={field.field}>{labelFor(field.field)} · {field.unit}</option>)}
                </select>
                <select aria-label={`Rule ${index + 1} operator`} value={rule.operator} onChange={(event) => updateRule(index, 'operator', event.target.value)} className="min-w-0 w-full rounded-lg border p-2 dark:border-slate-700 dark:bg-slate-900">
                  {operators.map((operator) => <option key={operator} value={operator}>{operator}</option>)}
                </select>
                {metadata?.kind === 'boolean' ? (
                  <select aria-label={`Rule ${index + 1} value`} value={String(rule.value)} onChange={(event) => updateRule(index, 'value', event.target.value === 'true')} className="rounded-lg border p-2 dark:border-slate-700 dark:bg-slate-900"><option value="false">No</option><option value="true">Yes</option></select>
                ) : metadata?.kind === 'classification' ? (
                  <div className="min-w-0">
                    <select
                      aria-label={`Rule ${index + 1} value`}
                      multiple={['in', 'not_in'].includes(rule.operator)}
                      value={['in', 'not_in'].includes(rule.operator) ? selectedValues : (rule.value || '')}
                      onChange={(event) => updateRule(index, 'value', ['in', 'not_in'].includes(rule.operator)
                        ? [...event.target.selectedOptions].map((option) => option.value)
                        : event.target.value)}
                      className="w-full rounded-lg border p-2 dark:border-slate-700 dark:bg-slate-900"
                    >
                      {unsupportedValues.map((value) => <option key={`unsupported-${value}`} value={value} disabled>Unsupported: {value}</option>)}
                      {allowedValues.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                    </select>
                    {parentDefinitionId && selected?.compatibility?.issues?.filter((issue) => (
                      issue.criterion_id === rule.id
                      && issue.suggested_value
                      && unsupportedValues.includes(issue.unsupported_value)
                    )).map((issue) => (
                      <button key={issue.unsupported_value} type="button" onClick={() => acceptSuggestion(issue)} className="mt-2 rounded-lg border border-amber-400 px-3 py-2 text-xs font-bold">Use {issue.suggested_label} in this child</button>
                    ))}
                  </div>
                ) : (
                  <input aria-label={`Rule ${index + 1} value`} type="number" step="any" value={rule.value} onChange={(event) => updateRule(index, 'value', event.target.value)} className="rounded-lg border p-2 dark:border-slate-700 dark:bg-slate-900" />
                )}
                <button type="button" disabled={rules.length === 1} onClick={() => setRules((current) => current.filter((_, ruleIndex) => ruleIndex !== index))} className="rounded-lg border px-3 text-sm font-bold disabled:opacity-40">Remove</button>
              </div>
            );
          })}
        </div>
        <button type="button" onClick={() => setRules((current) => [...current, newRule(catalog, current.length % catalog.fields.length)])} className="mt-3 rounded-lg border px-3 py-2 text-sm font-bold">Add rule</button>
        <details className="mt-4 rounded-xl border p-3">
          <summary className="cursor-pointer text-sm font-bold">Expert definition · Edit raw JSON</summary>
          <p className="mt-2 text-xs text-slate-500">Only the versioned declarative schema is accepted. Executable code is never allowed.</p>
          <textarea value={expertText} onChange={(event) => setExpertText(event.target.value)} rows={14} spellCheck="false" className="mt-3 w-full rounded-lg border p-3 font-mono text-xs dark:border-slate-700 dark:bg-slate-950" />
        </details>
        <button type="submit" disabled={working} className="mt-4 rounded-xl bg-blue-600 px-5 py-3 font-bold text-white disabled:opacity-50">{parentDefinitionId ? 'Save immutable child' : 'Save immutable definition'}</button>
      </form>

      <div className="rounded-2xl border border-slate-200 p-5 dark:border-slate-700">
        <h3 className="text-lg font-black">Evaluate saved definition</h3>
        <button type="button" onClick={evaluate} disabled={!selectedDefinitionId || working || selected?.lifecycle_state === 'ARCHIVED'} className="mt-3 rounded-xl bg-cyan-600 px-5 py-3 font-bold text-white disabled:opacity-50">Find matching stocks</button>
        {selected?.lifecycle_state === 'ARCHIVED' && <p className="mt-2 text-sm text-amber-800 dark:text-amber-300">Archived evidence cannot be evaluated through the active workflow. Restore it explicitly to evaluate again.</p>}
        {message && <p className="mt-3 rounded-lg bg-slate-100 p-3 text-sm dark:bg-slate-800">{message}</p>}
        {evaluation && evaluation.status !== 'evaluation_error' && (
          <div className="mt-4 rounded-xl border p-3 text-xs leading-5 dark:border-slate-700">
            <p><strong>Definition:</strong> {evaluation.definition_id}</p>
            <p className="break-all"><strong>Definition hash:</strong> {evaluation.definition_hash}</p>
            {evaluation.parent_definition_id && <p className="break-all"><strong>Parent:</strong> {evaluation.parent_definition_id}</p>}
            <p><strong>Catalog:</strong> {evaluation.catalog_schema_version} · {evaluation.vocabulary_version}</p>
            <p className="break-all"><strong>Generation:</strong> {evaluation.generation?.generation_id} · {evaluation.generation?.generation_hash}</p>
            <p><strong>Evaluation session:</strong> {evaluation.evaluation_date}</p>
            <p><strong>Matches:</strong> {evaluation.match_count}</p>
            <p><strong>Rejected for a missing required field:</strong> {evaluation.required_field_exclusion_count ?? 'Not evaluated because the definition is incompatible'}</p>
          </div>
        )}
        {candidates.length > 0 && (
          <div className="mt-4">
            <p role="status" aria-live="polite" aria-atomic="true" className="text-sm font-black text-blue-700 dark:text-blue-300">
              Selected: {selectedSymbols.length} {selectedSymbols.length === 1 ? 'stock' : 'stocks'}
            </p>
            <div className="mt-3 grid gap-2 sm:grid-cols-2">
              {candidates.map((candidate) => {
                const selectedCandidate = selectedSymbols.includes(candidate.symbol);
                return (
                  <article key={candidate.symbol} className="flex min-w-0 items-center justify-between gap-3 rounded-lg border p-3 text-sm dark:border-slate-700">
                    <div className="min-w-0">
                      <p className="font-black">{candidate.symbol}</p>
                      <p className="truncate text-xs text-slate-500">{candidate.company_name || 'Company'}</p>
                    </div>
                    {selectedCandidate ? (
                      <div className="flex shrink-0 items-center gap-2">
                        <span className="text-xs font-black text-emerald-700 dark:text-emerald-300">✓ Selected</span>
                        <button
                          type="button"
                          onClick={() => setSelectedSymbols((current) => current.filter((symbol) => symbol !== candidate.symbol))}
                          className="min-h-11 rounded-lg border px-3 py-2 text-xs font-bold dark:border-slate-700"
                          aria-label={`Remove ${candidate.company_name || candidate.symbol} (${candidate.symbol}) from bot setup`}
                        >
                          Remove
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setSelectedSymbols((current) => current.includes(candidate.symbol) ? current : [...current, candidate.symbol])}
                        className="min-h-11 shrink-0 rounded-lg bg-blue-600 px-4 py-2 text-xs font-bold text-white hover:bg-blue-700"
                        aria-label={`Select ${candidate.company_name || candidate.symbol} (${candidate.symbol}) for bot setup`}
                      >
                        Select
                      </button>
                    )}
                  </article>
                );
              })}
            </div>
            {onUseSymbols && <button type="button" disabled={!selectedSymbols.length} onClick={() => onUseSymbols(selectedSymbols, selected)} className="mt-4 rounded-xl border px-5 py-3 font-bold disabled:opacity-50">Use selected stocks in bot setup</button>}
          </div>
        )}
      </div>
    </section>
  );
};

export default CustomScreeningWorkspace;
